import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { presetRange, type DateBounds, type DatePreset } from "../../shared/overview";
import { civilToday } from "../../shared/time";
import { api } from "../api";
import { Banner, messageOf } from "./ui";

type Stat = { key: string; count: number; href: string | null };
type Dashboard = {
  today: string;
  range: DateBounds;
  stats_range: DateBounds;
  stats: Stat[];
};

const PRESETS: { id: DatePreset; label: string }[] = [
  { id: "today", label: "Σήμερα" },
  { id: "week", label: "Εβδομάδα" },
  { id: "month", label: "Μήνας" },
  { id: "year", label: "Έτος" },
  { id: "all", label: "Όλα" },
];

const STAT_LABELS: Record<string, string> = {
  new_inquiries: "Νέα αιτήματα",
  first_contact: "Πρώτη επαφή",
  confirmed: "Κλεισμένα event",
  deposit: "Εκκρεμότητες προκαταβολής",
  clients: "Πελάτες",
};

export function OverviewPage() {
  const [bounds, setBounds] = useState<DateBounds>(() => presetRange("today", civilToday("Europe/Athens")));
  const [statsSynced, setStatsSynced] = useState(false);
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const today = civilToday("Europe/Athens");
  const pressed = activePreset(bounds, today);

  useEffect(() => {
    let cancel = false;
    api<Dashboard>(dashboardPath(bounds, statsSynced))
      .then((result) => {
        if (!cancel) setData(result);
      })
      .catch((reason: unknown) => {
        if (!cancel) setError(messageOf(reason));
      });
    return () => {
      cancel = true;
    };
  }, [bounds, statsSynced]);

  function choose(preset: DatePreset) {
    setError(null);
    setStatsSynced(true);
    setBounds(presetRange(preset, civilToday("Europe/Athens")));
  }

  function changeBound(key: "from" | "to", value: string) {
    setError(null);
    setStatsSynced(true);
    setBounds((current) => ({ ...current, [key]: value || null }));
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Επισκόπηση</h1>
          <p className="lede">{data ? greekDate(data.today) : "Σήμερα στην Αθήνα."}</p>
        </div>
      </header>
      <div className="range-bar">
        <label className="range-field">Από
          <input type="date" value={bounds.from ?? ""} onChange={(event) => changeBound("from", event.target.value)} />
        </label>
        <label className="range-field">Έως
          <input type="date" value={bounds.to ?? ""} onChange={(event) => changeBound("to", event.target.value)} />
        </label>
        <div className="range-presets" role="group" aria-label="Περίοδος">
          {PRESETS.map((preset) => (
            <button key={preset.id} type="button" className="range-preset" aria-pressed={pressed === preset.id} onClick={() => choose(preset.id)}>
              {preset.label}
            </button>
          ))}
        </div>
      </div>
      <Banner error={error} />
      {!data && !error ? <p>Φόρτωση πίνακα…</p> : null}
      {data ? (
        <div className="overview">
          <section className="overview-section">
            <h2>Συνολικά στατιστικά</h2>
            {statsSynced ? <p className="meta">{rangeText(data.stats_range)}</p> : null}
            <div className="overview-stats">
              {data.stats.map((stat) => {
                const body = (<><span>{STAT_LABELS[stat.key] ?? stat.key}</span><b>{stat.count}</b></>);
                return stat.href
                  ? <Link className="card" key={stat.key} to={stat.href}>{body}</Link>
                  : <div className="card static" key={stat.key}>{body}</div>;
              })}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

function dashboardPath(bounds: DateBounds, statsSynced: boolean): string {
  const params = new URLSearchParams();
  if (!bounds.from && !bounds.to) params.set("all", "1");
  else {
    if (bounds.from) params.set("from", bounds.from);
    if (bounds.to) params.set("to", bounds.to);
  }
  if (statsSynced) {
    if (bounds.from) params.set("stats_from", bounds.from);
    if (bounds.to) params.set("stats_to", bounds.to);
  }
  const text = params.toString();
  return text ? `/api/dashboard?${text}` : "/api/dashboard";
}

function activePreset(bounds: DateBounds, today: string): DatePreset | null {
  for (const preset of PRESETS) {
    const range = presetRange(preset.id, today);
    if (range.from === bounds.from && range.to === bounds.to) return preset.id;
  }
  return null;
}

function rangeText(range: DateBounds): string {
  if (!range.from && !range.to) return "Όλα";
  if (range.from && range.to) return `${shortDate(range.from)} – ${shortDate(range.to)}`;
  if (range.from) return `από ${shortDate(range.from)}`;
  return `έως ${shortDate(range.to ?? "")}`;
}

function shortDate(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

function greekDate(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  return new Intl.DateTimeFormat("el-GR", { timeZone: "Europe/Athens", weekday: "long", day: "numeric", month: "long" }).format(date);
}
